/**
 * FinanzasApiClient -- HTTP client for /api/v1/businesses/{id}/{ingresos|egresos|creditos}
 *
 * V1 contract (mientras Argy-A entrega T2.0 endpoint agregado):
 *   GET    /api/v1/businesses/{businessId}/ingresos?period=&from=&to=&estado=&usuario=&producto=
 *   GET    /api/v1/businesses/{businessId}/ingresos/{id}
 *   GET    /api/v1/businesses/{businessId}/egresos?period=&from=&to=&estado=&concepto=&metodo=
 *   GET    /api/v1/businesses/{businessId}/egresos/{id}
 *   GET    /api/v1/businesses/{businessId}/creditos?period=&from=&to=&estado=&cliente=
 *   GET    /api/v1/businesses/{businessId}/creditos/{id}
 *   POST   /api/v1/businesses/{businessId}/creditos/{id}/abonos  body: { amount, metodo, notas, fechaOperativa? }
 *
 * Manejo graceful: 404 / endpoint no implementado -> { ok:false, error:{ code:'NOT_IMPLEMENTED' } }
 * Las vistas renderizan empty-state explicativo en ese caso (paridad UX cuando T2.0 no listo).
 *
 * @module infrastructure/api/FinanzasApiClient
 */

import { ApiBase } from './ApiBase.js';
import { queryDeFinanzas } from '../../application/finanzas/queryDeFinanzas.js';

const BASE = '/api/v1/businesses';

/**
 * Helper para componer query string de filtros estandar.
 * @param {object} f - { period, from, to, estado, usuario, producto, concepto, metodo, cliente, page, pageSize }
 */
function buildQuery(f = {}) {
  const qs = new URLSearchParams();
  if (f.period)   qs.set('period',   f.period);
  if (f.from)     qs.set('from',     f.from);
  if (f.to)       qs.set('to',       f.to);
  if (f.estado)   qs.set('estado',   f.estado);
  if (f.usuario)  qs.set('usuario',  f.usuario);
  if (f.producto) qs.set('producto', f.producto);
  if (f.concepto) qs.set('concepto', f.concepto);
  if (f.metodo)   qs.set('metodo',   f.metodo);
  if (f.medio)    qs.set('medio',    f.medio);
  if (f.cliente)  qs.set('cliente',  f.cliente);
  // A-T3: groupBy=venta (default backend, 1 fila/factura) | pago (1 fila/pago).
  if (f.groupBy)  qs.set('groupBy',  f.groupBy);
  // RB-T3 (contrato #2223): filtro egresos por rubro. 0/'' = todos.
  if (f.rubroId)  qs.set('rubroId',  String(f.rubroId));
  if (f.page)     qs.set('page',     String(f.page));
  if (f.pageSize) qs.set('pageSize', String(f.pageSize));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/**
 * Si el endpoint no esta listo aun, devolvemos empty-state estructurado para que
 * la vista renderice mensaje "T2.0 endpoint pendiente" sin crashear.
 */
function notImplementedFallback(_err) {
  return {
    ok: true,
    data: {
      items: [],
      totalCount: 0,
      page: 1,
      pageSize: 20,
      totales: { ingresoTotal: 0, egresoTotal: 0, saldoNeto: 0 },
      _hint: 'endpoint /api/v1/businesses/{id}/finanzas pendiente (T2.0 Argy-API)'
    }
  };
}

export const FinanzasApiClient = {
  // ============== INGRESOS ==============
  async listIngresos(businessId, filters = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };
    const res = await ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/ingresos${buildQuery(filters)}`);
    if (!res.ok && /not\s*found|404/i.test(JSON.stringify(res.error || ''))) {
      return notImplementedFallback(res.error);
    }
    return res;
  },

  async getIngresoById(businessId, id) {
    if (!businessId || !id) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };
    return ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/ingresos/${encodeURIComponent(id)}`);
  },

  // ============== EGRESOS ==============
  async listEgresos(businessId, filters = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };
    const res = await ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/egresos${buildQuery(filters)}`);
    if (!res.ok && /not\s*found|404/i.test(JSON.stringify(res.error || ''))) {
      return notImplementedFallback(res.error);
    }
    return res;
  },

  async getEgresoById(businessId, id) {
    if (!businessId || !id) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };
    return ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/egresos/${encodeURIComponent(id)}`);
  },

  /**
   * RB-T3 (contrato #2223): catalogo de rubros de egreso para poblar el dropdown
   * de filtro en #/egresos. Read-only — los rubros nacen en Desktop y suben por
   * sync (RB-D). GET /businesses/{id}/rubros-egreso -> { items:[{id,nombre,...}] }.
   * Devuelve [] si el endpoint aun no esta live (degrada sin romper el filtro).
   */
  async listRubrosEgreso(businessId) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };
    const res = await ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/rubros-egreso`);
    if (!res.ok) {
      if (/not\s*found|404/i.test(JSON.stringify(res.error || ''))) return { ok: true, items: [] };
      return res;
    }
    const d = res.data?.data ?? res.data ?? {};
    return { ok: true, items: Array.isArray(d.items) ? d.items : (Array.isArray(d) ? d : []) };
  },

  /**
   * RB-T3b (bit #3124): asigna o quita (rubroId=null) el rubro de un proveedor.
   * PATCH /businesses/{id}/proveedores/{proveedorId}/rubro (RubrosEgresoController).
   * Es la "memoria" proveedor->rubro: los egresos derivados de facturas de ese
   * proveedor heredan el rubro en LECTURA (COALESCE egreso.rubro_id, proveedor.rubro_id)
   * sin tocar egresos ya existentes.
   * @param {string} businessId
   * @param {string} proveedorId
   * @param {number|null} rubroId
   */
  async setProveedorRubro(businessId, proveedorId, rubroId) {
    if (!businessId || !proveedorId) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };
    const res = await ApiBase.patch(`${BASE}/${encodeURIComponent(businessId)}/proveedores/${encodeURIComponent(proveedorId)}/rubro`, { rubroId });
    if (!res.ok) return res;
    const d = res.data?.data ?? res.data ?? {};
    return { ok: true, data: { proveedorId: d.proveedorId, rubroId: d.rubroId ?? null, rubroNombre: d.rubroNombre || '' } };
  },

  // ============== CREDITOS ==============
  async listCreditos(businessId, filters = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };
    const res = await ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/creditos${buildQuery(filters)}`);
    if (!res.ok && /not\s*found|404/i.test(JSON.stringify(res.error || ''))) {
      return notImplementedFallback(res.error);
    }
    return res;
  },

  async getCreditoById(businessId, id) {
    if (!businessId || !id) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };
    return ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/creditos/${encodeURIComponent(id)}`);
  },

  /**
   * Registrar abono a un credito.
   * @param {string} businessId
   * @param {string} creditoId
   * @param {{ amount:number, metodo:string, notas?:string, fechaOperativa?:string }} payload
   */
  async registrarAbono(businessId, creditoId, payload) {
    if (!businessId || !creditoId) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };
    return ApiBase.post(`${BASE}/${encodeURIComponent(businessId)}/creditos/${encodeURIComponent(creditoId)}/abonos`, payload);
  },

  // ============== SALDOS (AGREGADO) ==============
  /**
   * Dashboard de SALDOS por negocio+periodo. Mision NEXO-API-SYNC-EXPAND-001.
   *
   * Agregador resiliente (se enciende progresivamente con el sync):
   *   1. Intenta el endpoint agregado GET /api/v1/businesses/{id}/finanzas
   *      (Argy-A lo migrara de Firestore a relacional, contrato T-ANALYSIS #1863).
   *   2. Si NO esta listo (404 / NOT_IMPLEMENTED / sin totales) cae a AGREGACION
   *      CLIENT-SIDE: lanza listIngresos + listEgresos + listCreditos en paralelo
   *      y suma sus totales. Asi el dashboard muestra los egresos reales (nexo_business.egresos)
   *      HOY aunque el agregado /finanzas aun no exista, y se completa al subir ventas/ingresos.
   *
   * Shape de retorno (alineado al contrato Argy-A #1863):
   *   { ok:true, data:{
   *       totales: { ingresoTotal, egresoTotal, saldoNeto, transferencias, creditosPorCobrar },
   *       source: 'aggregate' | 'client-side',
   *       partial: bool,        // true si alguna fuente fallo en el modo client-side
   *       _hint: string|null
   *   }}
   *
   * @param {string} businessId
   * @param {object} filters - { period, from, to }
   */
  async getSaldos(businessId, filters = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };

    // 1. Endpoint agregado (preferido cuando Argy-A lo entregue relacional)
    // PANEL-SALDOS-PERIODO-T1 (#15802): /finanzas lee periodo, desde y hasta; las listas de abajo siguen con buildQuery.
    const agg = await ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/finanzas${queryDeFinanzas(filters)}`);
    if (agg.ok) {
      const d = agg.data?.data ?? agg.data ?? {};
      const t = d.totales ?? d;
      if (t && (t.ingresoTotal != null || t.egresoTotal != null || t.saldoNeto != null)) {
        return {
          ok: true,
          data: {
            totales: {
              ingresoTotal:      Number(t.ingresoTotal ?? t.ingresos ?? 0),
              egresoTotal:       Number(t.egresoTotal ?? t.egresos ?? 0),
              saldoNeto:         Number(t.saldoNeto ?? ((t.ingresoTotal ?? 0) - (t.egresoTotal ?? 0))),
              transferencias:    Number(t.transferencias ?? 0),
              creditosPorCobrar: Number(t.creditosPorCobrar ?? t.creditos ?? 0)
            },
            source: 'aggregate',
            partial: false,
            _hint: d._hint ?? null
          }
        };
      }
    }

    // 2. Fallback: agregacion client-side desde las 3 listas.
    const [ingRes, egrRes, credRes] = await Promise.all([
      this.listIngresos(businessId, filters),
      this.listEgresos(businessId, filters),
      this.listCreditos(businessId, filters)
    ]);

    const pick = (res) => (res.ok ? (res.data?.data ?? res.data ?? {}) : {});
    const ing = pick(ingRes);
    const egr = pick(egrRes);
    const cred = pick(credRes);

    const ingT = ing.totales || {};
    const egrT = egr.totales || {};
    const credT = cred.totales || {};

    const ingresoTotal = Number(ingT.totalIngresos ?? ingT.ingresoTotal ?? 0);
    const egresoTotal  = Number(egrT.totalEgresos  ?? egrT.egresoTotal  ?? 0);
    // Transferencias: derivar de los items de ingresos por metodo (si vienen items).
    const ingItems = Array.isArray(ing.items) ? ing.items : [];
    const transferencias = ingItems
      .filter(i => String(i.metodo || '').toLowerCase() === 'transferencia')
      .reduce((s, i) => s + (Number(i.total) || 0), 0);
    const creditosPorCobrar = Number(credT.saldo ?? credT.totalPorCobrar ?? 0);

    // partial = alguna fuente que esperabamos no respondio con datos reales
    const anyEmpty = !ingRes.ok || !egrRes.ok || !credRes.ok
      || ing._hint || egr._hint || cred._hint;
    const hints = [ing._hint, egr._hint, cred._hint].filter(Boolean);

    return {
      ok: true,
      data: {
        totales: {
          ingresoTotal,
          egresoTotal,
          saldoNeto: ingresoTotal - egresoTotal,
          transferencias,
          creditosPorCobrar
        },
        source: 'client-side',
        partial: !!anyEmpty,
        _hint: hints.length
          ? 'Agregado client-side. Pendiente sync: ' + hints.join(' · ')
          : 'Agregado client-side (sumando ingresos/egresos/creditos por separado).'
      }
    };
  },

  // ============== SALDOS ACUMULADOS POR MEDIO (B-T3) ==============
  /**
   * SALDOS-RELACIONAL-V1-001 B-T3 -- saldo ACUMULADO por medio de pago.
   *
   * Backend Argy-A (nexo-api PR #83 + fix ff1866c, contrato REAL leido del
   * SaldosRelacionalController/SaldosBaseService/SaldosRelacionalDtos):
   *   GET /api/v1/businesses/{id}/saldos?hasta=YYYY-MM-DD
   *   -> data: { hasta, porMedio: [{ medio, base, fecha_base, ingresos, egresos, saldo }],
   *              egresos_sin_clasificar, saldo_total }
   *
   * Formula por medio (NO Firestore, decision Josse #1919):
   *   saldo = base + SUM(venta_pagos del medio, fecha >= fecha_base y <= hasta)
   *               - SUM(egresos con metodo mapeado: {efectivo,caja}->efectivo,
   *                     {transferencia,banco}->transferencia)
   * Egresos con metodo externo/tarjeta/null NO restan a ningun medio (paridad
   * FrmArqueoV15): llegan aparte en egresos_sin_clasificar y el server ya los
   * resta en saldo_total.
   *
   * Sin fila en saldos_base => base=0 desde 1900-01-01 (acumulado historico),
   * asi que porMedio SIEMPRE trae efectivo+transferencia con el endpoint vivo.
   *
   * Mapea al shape interno estable que consume SaldosView:
   *   { medios:[{medio,fechaBase,montoBase,entradas,salidas,saldo}],
   *     totalSaldo, egresosSinClasificar, hasta }
   *
   * @param {string} businessId
   * @param {{ hasta?: string }} opts - corte superior opcional (ISO date, default hoy server-side)
   */
  async getSaldosPorMedio(businessId, { hasta } = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };
    const qs = hasta ? `?hasta=${encodeURIComponent(hasta)}` : '';
    const res = await ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/saldos${qs}`);
    if (!res.ok) {
      // PANEL-SALDOS-PERIODO-T1: ApiBase deja el status en res.code y el texto ya traducido en
      // res.error; mirar solo el texto dejaba el respaldo de flujo sin correr nunca.
      if (res.code === 404 || /not\s*found|404/i.test(JSON.stringify(res.error || ''))) {
        return { ok: false, error: { code: 'NOT_IMPLEMENTED' } };
      }
      return res;
    }
    const d = res.data?.data ?? res.data ?? {};
    const raw = Array.isArray(d.porMedio) ? d.porMedio : [];
    const medios = raw.map(m => ({
      medio:     String(m.medio ?? 'sin_clasificar'),
      fechaBase: m.fecha_base ?? null,
      montoBase: Number(m.base) || 0,
      entradas:  Number(m.ingresos) || 0,
      salidas:   Number(m.egresos) || 0,
      saldo:     Number(m.saldo) || 0
    }));
    const egresosSinClasificar = Number(d.egresos_sin_clasificar) || 0;
    // saldo_total server-side = SUM(saldo por medio) - egresos_sin_clasificar.
    const totalSaldo = Number(d.saldo_total ?? (medios.reduce((s, m) => s + m.saldo, 0) - egresosSinClasificar));
    return { ok: true, data: { medios, totalSaldo, egresosSinClasificar, hasta: d.hasta ?? hasta ?? null } };
  },

  /**
   * SALDOS-DIA-T1 (nexo-api 395): el saldo dia por dia y por medio desde la base.
   *   GET /api/v1/businesses/{id}/saldos/diario?desde=&hasta=&medio=
   * Sin desde arranca en la base mas reciente. 404 -> NOT_IMPLEMENTED (la vista lo dice y no pinta la tabla).
   * La forma la lee domain/saldosDia.js (diasDesdeRespuesta).
   */
  async getSaldosDiario(businessId, { desde, hasta, medio } = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };
    const qs = new URLSearchParams();
    if (desde) qs.set('desde', desde);
    if (hasta) qs.set('hasta', hasta);
    if (medio) qs.set('medio', medio);
    const q = qs.toString();
    const res = await ApiBase.get(`${BASE}/${encodeURIComponent(businessId)}/saldos/diario${q ? `?${q}` : ''}`);
    if (!res.ok && res.code === 404) return { ok: false, code: 'NOT_IMPLEMENTED', error: { code: 'NOT_IMPLEMENTED' } };
    return res;
  },

  /**
   * B-T3 UI "fijar base": setea/actualiza la base de un medio (snapshot del
   * efectivo/banco contado a una fecha).
   *
   * GAP DE CONTRATO (reportado a Ares en bitacora): el spec B-T3 (task 057632ef
   * step 3) pide "UI fijar base (PUT saldos-base)" pero nexo-api PR #83 NO
   * incluye endpoint de escritura -- la tabla saldos_base es seed "manual/panel"
   * segun su migration. Ruta asumida hasta que Argy-A la publique:
   *   PUT /api/v1/businesses/{id}/saldos/base   body { medio, fechaBase, montoBase }
   * Mientras no exista, devuelve NOT_IMPLEMENTED y la UI degrada con mensaje
   * honesto (la base se puede sembrar via SQL con Ares).
   */
  async setSaldoBase(businessId, { medio, fechaBase, montoBase } = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };
    if (!medio) return { ok: false, error: { code: 'MEDIO_REQUIRED' } };
    const body = {
      medio: String(medio),
      fechaBase: fechaBase || new Date().toISOString().slice(0, 10),
      montoBase: Number(montoBase) || 0
    };
    const res = await ApiBase.put(`${BASE}/${encodeURIComponent(businessId)}/saldos/base`, body);
    if (!res.ok && /not\s*found|404|405/i.test(JSON.stringify(res.error || ''))) {
      return { ok: false, error: { code: 'NOT_IMPLEMENTED' } };
    }
    return res;
  },

  /**
   * Helper publico para reusar en vistas: traduce period code -> from/to ISO date.
   */
  resolvePeriodRange(period) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const fmt = (d) => d.toISOString().slice(0, 10);
    switch (period) {
      case 'hoy': {
        return { from: fmt(today), to: fmt(today) };
      }
      case 'semana': {
        const start = new Date(today); start.setDate(today.getDate() - today.getDay());
        return { from: fmt(start), to: fmt(today) };
      }
      case 'mes': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: fmt(start), to: fmt(today) };
      }
      case 'total':
      default:
        return { from: undefined, to: undefined };
    }
  }
};
